const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_ROWS = 10_000;

export type ParsedSheet = {
  headers: string[];
  rows: string[][];
};

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

export function parseCsvText(text: string): ParsedSheet {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (!lines.length) throw new Error('Empty file');
  const headers = parseCsvLine(lines[0]!);
  const rows = lines.slice(1).map(parseCsvLine);
  if (rows.length > MAX_ROWS) {
    throw new Error(`Too many rows (max ${MAX_ROWS})`);
  }
  return { headers, rows };
}

export async function parseSpreadsheetFile(file: File): Promise<ParsedSheet> {
  if (file.size > MAX_FILE_BYTES) {
    throw new Error('File too large (max 20 MB). Split the file or use CSV.');
  }

  const name = file.name.toLowerCase();
  if (name.endsWith('.csv') || file.type === 'text/csv') {
    const text = await file.text();
    return parseCsvText(text);
  }

  if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
    const XLSX = await import('xlsx');
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheetName = wb.SheetNames[0];
    if (!sheetName) throw new Error('No sheet in workbook');
    const sheet = wb.Sheets[sheetName]!;
    const aoa = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, defval: '' });
    if (!aoa.length) throw new Error('Empty sheet');
    const headers = (aoa[0] ?? []).map((c) => String(c).trim());
    const rows = aoa.slice(1).map((r) => headers.map((_, i) => String(r[i] ?? '').trim()));
    if (rows.length > MAX_ROWS) {
      throw new Error(`Too many rows (max ${MAX_ROWS})`);
    }
    return { headers, rows };
  }

  throw new Error('Unsupported format. Use CSV or Excel (.xlsx).');
}
